import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AlbumFont, AlbumTextAlign } from '../../types';
import { fontCss } from '../../utils/albumFonts';

interface Props {
  kind: 'TITLE' | 'TEXT';
  initial: string;
  maxChars: number;
  /** The same size the page prints it at, so what you type is what you get. */
  fontSize: string;
  font: AlbumFont;
  align: AlbumTextAlign;
  muted: string;
  onCommit: (text: string | null) => void;
}

/**
 * Typing straight onto the page. Leaving the box saves; Esc puts back what was
 * there. A title is one line — Enter saves it; a text keeps its line breaks.
 */
export default function AlbumInlineText({ kind, initial, maxChars, fontSize, font, align, muted, onCommit }: Props) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(initial);
  const ref = useRef<HTMLTextAreaElement>(null);
  const cancelled = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, []);

  function commit() {
    if (cancelled.current) return;
    const next = draft.trim();
    if (next !== initial.trim()) onCommit(next || null);
  }

  return (
    <div className="relative w-full h-full">
      <textarea
        ref={ref}
        value={draft}
        maxLength={Math.max(maxChars, initial.length)}
        onChange={e => setDraft(kind === 'TITLE' ? e.target.value.replace(/\n/g, ' ') : e.target.value)}
        onBlur={commit}
        onClick={e => e.stopPropagation()}
        onKeyDown={e => {
          e.stopPropagation();
          if (e.key === 'Escape') { cancelled.current = true; setDraft(initial); e.currentTarget.blur(); }
          if (e.key === 'Enter' && (kind === 'TITLE' || e.metaKey || e.ctrlKey)) { e.preventDefault(); e.currentTarget.blur(); }
        }}
        aria-label={t(`album.slot.${kind}`)}
        placeholder={t(`album.slot.${kind}`)}
        className={`w-full h-full resize-none bg-transparent outline-none overflow-hidden p-0 m-0 border-0 text-inherit placeholder:opacity-50 ${
          kind === 'TITLE' ? 'leading-tight' : 'leading-snug'
        }`}
        style={{ fontSize, color: 'inherit', textAlign: align, ...fontCss(font) }}
      />
      <span className="absolute bottom-0.5 right-1 font-mono text-[9px] tabular-nums pointer-events-none" style={{ color: draft.length > maxChars ? '#ef4444' : muted }}>
        {t('album.chars', { count: draft.length, max: maxChars })}
      </span>
    </div>
  );
}
